/* Movora cart drawer behaviour: discount codes and the upsell Add button.
 *
 * Dawn's assets/cart.js and cart-drawer.js are NOT modified. They already own
 * open/close, quantity changes and removal. This file adds only the two things
 * Dawn has no equivalent for.
 *
 * EVERY LISTENER IS DELEGATED FROM `document`. The drawer re-renders by
 * replacing `cart-drawer-items` and `.cart-drawer__footer` wholesale, so a
 * listener bound directly to the Apply button is destroyed by the first
 * successful apply. The symptom is a button that works exactly once and then
 * silently stops, which reads as an intermittent bug rather than a lifecycle
 * one.
 *
 * DISCOUNTS. Shopify exposes no AJAX endpoint for applying a cart discount. The
 * only supported route is a plain GET to /discount/<code>, which sets it on the
 * session and then redirects. Fetching it is enough; the redirect target is
 * discarded. Note /discount/A,B is read as ONE literal code and comes back
 * applicable:false -- codes only stack when typed at checkout, so this applies
 * one at a time and says so.
 *
 * Removal has no supported endpoint at all. The button tries the update route
 * and then VERIFIES against /cart.js. If the code is still there it says so
 * plainly instead of leaving a chip that looks removed and is not.
 */
(function () {
  'use strict';

  var CART = (window.routes && window.routes.cart_url) || '/cart';

  function noteEl() {
    return document.querySelector('[data-mv-discount-note]');
  }

  function say(msg, isError) {
    var el = noteEl();
    if (!el) return;
    el.textContent = msg;
    el.classList.toggle('is-error', !!isError);
  }

  function busy(el, state) {
    if (!el) return;
    if (state) el.setAttribute('aria-busy', 'true');
    else el.removeAttribute('aria-busy');
  }

  /* Re-render the whole drawer body from the section, then re-bind the overlay.
   * Replacing #CartDrawer innerHTML (rather than just the two inner nodes)
   * is what makes an empty -> filled transition work: `cart-drawer-items` does
   * not exist in the empty state, so a targeted swap would find nothing to
   * replace and quietly leave the empty view on screen. */
  function refreshDrawer() {
    return fetch(CART + '?section_id=cart-drawer')
      .then(function (r) {
        return r.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var src = doc.querySelector('#CartDrawer');
        var dst = document.querySelector('#CartDrawer');
        if (!src || !dst) return;

        dst.innerHTML = src.innerHTML;

        var drawer = document.querySelector('cart-drawer');
        if (drawer) {
          drawer.classList.toggle('is-empty', !!src.querySelector('.drawer__inner-empty'));
          var overlay = drawer.querySelector('#CartDrawer-Overlay');
          if (overlay) {
            overlay.addEventListener('click', function () {
              drawer.close();
            });
          }
        }
        return refreshBubble();
      });
  }

  function refreshBubble() {
    return fetch(CART + '?section_id=cart-icon-bubble')
      .then(function (r) {
        return r.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var src = doc.querySelector('.shopify-section');
        var dst = document.getElementById('cart-icon-bubble');
        if (src && dst) dst.innerHTML = src.innerHTML;
      })
      .catch(function () {
        /* The bubble count is cosmetic. Never let it fail the operation that
         * actually changed the cart. */
      });
  }

  function currentCodes() {
    return fetch(CART + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (c) {
        return (c.discount_codes || []).filter(function (d) {
          return d.applicable;
        });
      })
      .catch(function () {
        return null;
      });
  }

  function applyCode(code, btn) {
    if (!code) {
      say('Enter a code first.', true);
      return;
    }
    if (code.indexOf(',') !== -1) {
      say('One code at a time. Multiple codes stack at checkout.', true);
      return;
    }

    busy(btn, true);
    say('Applying...', false);

    fetch('/discount/' + encodeURIComponent(code), { method: 'GET' })
      .then(function () {
        return currentCodes();
      })
      .then(function (codes) {
        return refreshDrawer().then(function () {
          if (codes === null) {
            say('Could not confirm the code. It will be checked at checkout.', true);
            return;
          }
          var hit = codes.some(function (d) {
            return d.code.toUpperCase() === code.toUpperCase();
          });
          if (hit) say('Code applied.', false);
          else say('That code is not valid for this bag.', true);
        });
      })
      .catch(function () {
        say('Could not reach the store. Try again.', true);
      })
      .finally(function () {
        busy(btn, false);
      });
  }

  function removeCode(code, btn) {
    busy(btn, true);
    fetch(CART + '/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: '' }),
    })
      .then(function () {
        return currentCodes();
      })
      .then(function (codes) {
        return refreshDrawer().then(function () {
          var stillThere =
            codes &&
            codes.some(function (d) {
              return d.code.toUpperCase() === code.toUpperCase();
            });
          if (stillThere) {
            say('That code stays on the bag until checkout, where it can be changed.', true);
          }
        });
      })
      .catch(function () {
        say('Could not remove that code. It can be changed at checkout.', true);
      })
      .finally(function () {
        busy(btn, false);
      });
  }

  function addVariant(id, btn) {
    busy(btn, true);
    fetch(CART + '/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return refreshDrawer();
      })
      .catch(function () {
        var errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = 'That item could not be added. Try its own page.';
      })
      .finally(function () {
        busy(btn, false);
      });
  }

  document.addEventListener('click', function (event) {
    var apply = event.target.closest('[data-mv-discount-apply]');
    if (apply) {
      event.preventDefault();
      var input = document.getElementById('MvDiscountCode');
      applyCode(input ? input.value.trim() : '', apply);
      return;
    }

    var remove = event.target.closest('[data-mv-discount-remove]');
    if (remove) {
      event.preventDefault();
      removeCode(remove.getAttribute('data-mv-discount-remove'), remove);
      return;
    }

    var add = event.target.closest('[data-mv-add]');
    if (add) {
      event.preventDefault();
      addVariant(add.getAttribute('data-mv-add'), add);
    }
  });

  /* Enter inside the code field must not submit CartDrawer-Form -- that posts
   * the cart and navigates away from the drawer mid-edit. */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    if (!event.target.matches || !event.target.matches('#MvDiscountCode')) return;
    event.preventDefault();
    applyCode(event.target.value.trim(), document.querySelector('[data-mv-discount-apply]'));
  });
})();
